import { IoInformationCircle } from 'react-icons/io5'

function formatGold(value) {
    const numericValue = Number(value || 0)

    if (Math.abs(numericValue) >= 1000) {
        return `${(numericValue / 1000).toFixed(1).replace(/\.0$/, '')}k`
    }

    return `${Math.round(numericValue)}`
}

function formatLead(value) {
    return formatGold(Math.abs(Number(value) || 0))
}

function leadSide(value, playerTeamId) {
    if (playerTeamId === 100 || playerTeamId === 200) {
        return Number(value) >= 0 ? 'Your team' : 'Opponent'
    }

    return Number(value) >= 0 ? 'Blue side' : 'Red side'
}

function formatMinute(value) {
    const numericValue = Number(value || 0)
    return Number.isInteger(numericValue) ? `${numericValue}m` : `${numericValue.toFixed(1)}m`
}

const WIN_COLOR = 'rgb(34 211 238)'
const LOSS_COLOR = 'rgb(251 113 133)'
const NEUTRAL_COLOR = 'rgb(161 161 170)'
const AXIS_LABEL_COLOR = 'rgb(161 161 170)'
const AXIS_ZERO_COLOR = 'rgb(212 212 216)'
const GRID_COLOR = 'rgb(63 63 70)'
const ZERO_LINE_COLOR = 'rgb(161 161 170)'

function TeamGoldChart({ playerTeamId, teamState }) {
    const checkpoints = Array.isArray(teamState?.checkpoints)
        ? teamState.checkpoints.filter((checkpoint) => (
            Number.isFinite(Number(checkpoint?.minute))
            && Number.isFinite(Number(checkpoint?.rawGoldLead))
            && Number.isFinite(Number(checkpoint?.effectiveGoldLead))
        ))
        : []

    if (!checkpoints.length) {
        return (
            <section className="rounded-xl border border-zinc-800 bg-zinc-950/80 px-3 py-4 text-center text-zinc-400" aria-label="Team gold lead chart unavailable">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-300">Team gold lead over time</div>
                <div className="mt-2 text-xs">This match does not have team lead checkpoints available.</div>
            </section>
        )
    }

    const width = 760
    const height = 290
    const left = 52
    const right = 52
    const top = 16
    const bottom = 40
    const chartWidth = width - left - right
    const chartHeight = height - top - bottom
    const hasPlayerPerspective = playerTeamId === 100 || playerTeamId === 200
    const relativeLead = (value) => (playerTeamId === 200 ? -Number(value) : Number(value))
    const colorForLead = (value) => {
        if (hasPlayerPerspective) {
            if (Number(value) > 0) {
                return WIN_COLOR
            }

            if (Number(value) < 0) {
                return LOSS_COLOR
            }

            return NEUTRAL_COLOR
        }

        return NEUTRAL_COLOR
    }
    const rawScale = Math.ceil(Math.max(
        1_000,
        ...checkpoints.map((checkpoint) => Math.abs(relativeLead(checkpoint.rawGoldLead))),
    ) / 1_000) * 1_000
    const effectiveScale = Math.ceil(Math.max(
        1_000,
        ...checkpoints.map((checkpoint) => Math.abs(relativeLead(checkpoint.effectiveGoldLead))),
    ) / 1_000) * 1_000
    const xStep = checkpoints.length > 1 ? chartWidth / (checkpoints.length - 1) : chartWidth
    const xPosition = (index) => left + (index * xStep)
    const yZero = top + chartHeight / 2
    const yPosition = (value, scale) => yZero - ((Number(value) / scale) * (chartHeight / 2))
    const normalizedTicks = [1, 0.5, 0, -0.5, -1]
    const rawPoints = checkpoints.map((checkpoint, index) => ({
        x: xPosition(index),
        y: yPosition(relativeLead(checkpoint.rawGoldLead), rawScale),
        value: relativeLead(checkpoint.rawGoldLead),
    }))
    const effectivePoints = checkpoints.map((checkpoint, index) => ({
        x: xPosition(index),
        y: yPosition(relativeLead(checkpoint.effectiveGoldLead), effectiveScale),
        value: relativeLead(checkpoint.effectiveGoldLead),
    }))
    const segmentPath = (points, index) => `M ${points[index].x} ${points[index].y} L ${points[index + 1].x} ${points[index + 1].y}`
    const areaPath = (points, index) => {
        const start = points[index]
        const end = points[index + 1]
        return `M ${start.x} ${yZero} L ${start.x} ${start.y} L ${end.x} ${end.y} L ${end.x} ${yZero} Z`
    }

    return (
        <section className="rounded-xl border border-zinc-800 bg-zinc-950/80 px-3 py-3 text-zinc-200" aria-label="Team gold lead and True Lead over time">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-300">Team gold lead over time</div>
            </div>
            <div className="mb-1 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-zinc-400">
                <span className="inline-flex h-4 items-center gap-1.5 leading-4">
                    <span aria-hidden="true" className="inline-flex h-4 w-5 items-center justify-center">
                        <span className="flex w-5">
                            <span className="block h-0 w-1/2 border-t-[3px] border-cyan-400" />
                            <span className="block h-0 w-1/2 border-t-[3px] border-rose-400" />
                        </span>
                    </span>
                    <span className="leading-4">GOLD LEAD</span>
                </span>
                <span className="inline-flex h-4 items-center gap-1.5 leading-4">
                    <span aria-hidden="true" className="inline-flex h-4 w-5 items-center justify-center">
                        <span className="flex w-5">
                            <span className="block h-0 w-1/2 border-t-2 border-dashed border-cyan-400" />
                            <span className="block h-0 w-1/2 border-t-2 border-dashed border-rose-400" />
                        </span>
                    </span>
                    <span className="leading-4">TRUE LEAD</span>
                    <span className="group relative inline-flex h-4 w-4 items-center justify-center">
                        <button
                            aria-label="True Lead information"
                            className="inline-flex h-4 w-4 items-center justify-center p-0 text-zinc-500 transition-colors hover:text-zinc-300"
                            type="button"
                        >
                            <IoInformationCircle aria-hidden="true" className="block h-3.5 w-3.5" />
                        </button>
                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-52 -translate-x-1/2 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-left text-[10px] font-medium normal-case leading-snug tracking-normal text-zinc-300 shadow-xl group-hover:block group-focus-within:block">
                            True Lead estimates the effective advantage in gold-equivalent units. It accounts for non-gold values such as levels and objectives.
                        </span>
                    </span>
                </span>
            </div>
            <svg className="block h-auto w-full" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Team gold lead and True Lead over time">
                {normalizedTicks.map((ratio) => {
                    const y = yZero - (ratio * chartHeight / 2)

                    return (
                        <g key={ratio}>
                            <line x1={left} x2={width - right} y1={y} y2={y} stroke={ratio === 0 ? ZERO_LINE_COLOR : GRID_COLOR} strokeWidth={ratio === 0 ? '1.5' : '1'} strokeOpacity={ratio === 0 ? '0.9' : '0.65'} />
                            <text fill={ratio === 0 ? AXIS_ZERO_COLOR : AXIS_LABEL_COLOR} fontSize="10" fontWeight="600" textAnchor="end" x={left - 7} y={y + 3}>
                                {formatGold(Math.abs(rawScale * ratio))}
                            </text>
                            <text fill={ratio === 0 ? AXIS_ZERO_COLOR : AXIS_LABEL_COLOR} fontSize="10" fontWeight="600" textAnchor="start" x={width - right + 7} y={y + 3}>
                                {formatGold(Math.abs(effectiveScale * ratio))}
                            </text>
                        </g>
                    )
                })}
                {rawPoints.slice(0, -1).map((point, index) => (
                    <path
                        d={areaPath(rawPoints, index)}
                        fill={colorForLead((point.value + rawPoints[index + 1].value) / 2)}
                        fillOpacity="0.14"
                        key={`area-${checkpoints[index].minute}`}
                    />
                ))}
                {rawPoints.slice(0, -1).map((point, index) => (
                    <path
                        d={segmentPath(rawPoints, index)}
                        fill="none"
                        key={`raw-${checkpoints[index].minute}`}
                        stroke={colorForLead((point.value + rawPoints[index + 1].value) / 2)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.75"
                    />
                ))}
                {effectivePoints.slice(0, -1).map((point, index) => (
                    <path
                        d={segmentPath(effectivePoints, index)}
                        fill="none"
                        key={`effective-${checkpoints[index].minute}`}
                        stroke={colorForLead((point.value + effectivePoints[index + 1].value) / 2)}
                        strokeDasharray="5 4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeOpacity="0.72"
                        strokeWidth="1.75"
                    />
                ))}
                {rawPoints.map((point, index) => (
                    <circle
                        cx={point.x}
                        cy={point.y}
                        fill={colorForLead(point.value)}
                        key={`raw-point-${checkpoints[index].minute}`}
                        r="3"
                        stroke="rgb(9 9 11)"
                        strokeWidth="1"
                    >
                        <title>{`${formatMinute(checkpoints[index].minute)}: gold lead ${formatLead(point.value)} (${leadSide(point.value, playerTeamId)})`}</title>
                    </circle>
                ))}
                {effectivePoints.map((point, index) => (
                    <circle
                        cx={point.x}
                        cy={point.y}
                        fill="rgb(9 9 11)"
                        key={`effective-point-${checkpoints[index].minute}`}
                        r="2.5"
                        stroke={colorForLead(point.value)}
                        strokeWidth="1.5"
                    >
                        <title>{`${formatMinute(checkpoints[index].minute)}: True Lead ${formatLead(point.value)} (${leadSide(point.value, playerTeamId)})`}</title>
                    </circle>
                ))}
                {checkpoints.map((checkpoint, index) => (
                    (index % 2 === 0 || index === checkpoints.length - 1) ? (
                        <text
                            fill={AXIS_LABEL_COLOR}
                            fontSize="9"
                            fontWeight="600"
                            key={`minute-${checkpoint.minute}-${checkpoint.timestamp || index}`}
                            textAnchor="middle"
                            x={xPosition(index)}
                            y={height - 10}
                        >
                            {index === checkpoints.length - 1 ? 'END' : formatMinute(checkpoint.minute)}
                        </text>
                    ) : null
                ))}
            </svg>
        </section>
    )
}

export default TeamGoldChart
